// Internationalization translations
// To add a new language, add a new key to the translations object

import { en } from './en'
import { es } from './es'

export type Language = 'en' | 'es'

export interface Translations {
  // Header
  title: string
  subtitle: string
  settings: string

  // Resources
  resources: string
  money: string
  energy: string
  compute: string
  quality: string
  totalUsers: string
  price: string
  research: string

  // Actions
  train: string
  trainCost: string
  qualityGain: string
  inference: string
  needUsers: string
  usersWaiting: string
  qualityToAttract: string
  researchAction: string
  unlocksAfter: string
  computeSpent: string
  cost: string

  // Hardware
  hardware: string
  computePerSec: string
  usesEnergy: string
  devicesTotal: string

  // Models
  training: string
  yourModels: string
  params: string
  maxUsers: string
  trainCost2: string
  unlocked: string
  researchPoints: string

  // Upgrades
  upgrades: string
  upgradesLockedMsg: string
  max: string

  // Footer
  computeSpentStat: string
  time: string
  resetGame: string
  resetConfirm: string
  pause: string
  resume: string

  // Event Log
  eventLog: string
  welcome: string

  // Price
  market: string
  pricePerMillion: string

  // Tags
  tagEnergy: string
  tagCompute: string
  tagHw: string
  tagTrain: string
  tagResearch: string
  tagAuto: string

  // Tutorial messages
  tutorialWelcome: string
  tutorialFirstTrain: string
  tutorialQuality20: string
  tutorialQuality40: string
  tutorialQuality50: string
  tutorialFirstUsers: string
  tutorialUsers10: string
  tutorialMoney100: string
  tutorialMoney1k: string
  tutorialResearchUnlock: string
  tutorialFirstResearch: string
  tutorialGpt2Small: string
  tutorialGpt2Large: string
  tutorialGpt3: string
  tutorialGpt4: string
  tutorialAgi: string
  tutorialHwGaming: string
  tutorialHwServer: string
  tutorialHwDatacenter: string
  tutorialHwSatellite: string
  tutorialHwQuantum: string
  tutorialUsers100: string
  tutorialUsers1k: string
  tutorialUsers100k: string
  tutorialUsers1m: string

  // Model descriptions
  modelTinyDesc: string
  modelGpt2SmallDesc: string
  modelGpt2LargeDesc: string
  modelGpt3Desc: string
  modelGpt4Desc: string
  modelAgiDesc: string

  // Upgrade names and descriptions
  upgradeLaptopOverclock: string
  upgradeLaptopOverclockDesc: string
  upgradeBetterOutlet: string
  upgradeBetterOutletDesc: string
  upgradeSolarPanel: string
  upgradeSolarPanelDesc: string
  upgradePowerGrid: string
  upgradePowerGridDesc: string
  upgradeNuclearPlant: string
  upgradeNuclearPlantDesc: string
  upgradeFusionReactor: string
  upgradeFusionReactorDesc: string
  upgradeDysonSphere: string
  upgradeDysonSphereDesc: string
  upgradeRamUpgrade: string
  upgradeRamUpgradeDesc: string
  upgradeSsdArray: string
  upgradeSsdArrayDesc: string
  upgradeBetterOptimizer: string
  upgradeBetterOptimizerDesc: string
  upgradeResearchPapers: string
  upgradeResearchPapersDesc: string
  upgradeSelfServicePortal: string
  upgradeSelfServicePortalDesc: string
  upgradeApiAccess: string
  upgradeApiAccessDesc: string
  upgradeMobileApp: string
  upgradeMobileAppDesc: string
  upgradeEnterpriseTier: string
  upgradeEnterpriseTierDesc: string
  upgradeGlobalCdn: string
  upgradeGlobalCdnDesc: string
  upgradeBasicChatbot: string
  upgradeBasicChatbotDesc: string

  // Hardware upgrade descriptions
  hwUsedGpuDesc: string
  hwGamingGpuDesc: string
  hwProGpuDesc: string
  hwWorkstationDesc: string
  hwServerRackDesc: string
  hwGpuClusterDesc: string
  hwMiniDatacenterDesc: string
  hwDatacenterDesc: string
  hwMegaDatacenterDesc: string
  hwDatacenterNetworkDesc: string
  hwSupercomputerDesc: string
  hwSatelliteDesc: string
  hwQuantumDesc: string

  // Misc
  purchased: string
  switchedTo: string
  unlockedModel: string
  scalesToUsers: string
  trainItToAttract: string
  unlimited: string
  language: string
  
  // Mobile labels
  mobileResources: string
  mobileModels: string
  mobileHardware: string
  mobileUpgrades: string
  sell: string
  sellFor: string
  discontinueConfirm: string
  autoInference: string
  autoInferenceRate: string
  autoTrain: string
  autoTrainRate: string
  autoResearch: string
  autoResearchRate: string

  // Tooltips
  tooltipTitle: string
  tooltipMoney: string
  tooltipEnergy: string
  tooltipCompute: string
  tooltipResearch: string
  tooltipUsers: string
  tooltipComputeSpent: string
  tooltipPlayTime: string
  tooltipSettings: string

  // Share
  shareButton: string
  shareText: string
  shareCopied: string
  imageCopied: string
  shareChallenge: string
  shareTitle: string
  shareDownload: string
  model: string

  // Feedback
  feedback: string
  feedbackTitle: string
  feedbackPlaceholder: string
  feedbackEmailPlaceholder: string
  feedbackSend: string
  feedbackSending: string
  feedbackSuccess: string
  feedbackError: string
  feedbackRateLimit: string
  feedbackClose: string

  // Console messages
  consoleGameLoaded: string
  consoleGameSaved: string
  consoleFailedToSave: string
  consoleFailedToLoad: string
  consoleFailedToClear: string

  // Victory
  victoryTitle: string
  victorySubtitle: string
  victoryTime: string
  victoryCta: string
  victoryClose: string
}

export const translations: Record<Language, Translations> = {
  en,
  es,
}

// Get translation by key
export function t(key: keyof Translations, lang: Language): string {
  return translations[lang][key] || translations.en[key] || key
}
